import { UserRole } from '@/domain/auth/auth'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { formatClientStatus, getClientStatusColor } from '@/domain/clients/client'
import { useDeleteClient } from '@/hooks/api/useClients'
import { useToast } from '@/hooks/use-toast'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'

export const ClientHeader = ({ clientId, client }: { clientId: string; client: any }) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()
  const deleteMutation = useDeleteClient()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole)
  const canEdit = hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['agreements'] }),
        queryClient.invalidateQueries({ queryKey: ['parcels'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
      ])

      toast({
        title: 'Datos actualizados',
        description: 'Se han recargado los datos del cliente',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDelete = () => {
    if (confirm('¿Estás seguro de que quieres eliminar este cliente? Esta acción no se puede deshacer.')) {
      deleteMutation.mutate(clientId, {
        onSuccess: () => navigate({ to: `${Paths.Management.Clients}` })
      })
    }
  }

  return (
    <NavHeader
      actions={
        <div className="flex items-center gap-2">
          {canEdit && (
            <IconButton
              variant="default"
              title="Refrescar datos"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </IconButton>
          )}
          {canEdit && (
            <IconButton
              variant="danger"
              title="Eliminar cliente"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </IconButton>
          )}
        </div>
      }
    >
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
        Cliente N° {client.userData?.code}
        <Badge className={getClientStatusColor(client.status)}>
          {formatClientStatus(client.status)}
        </Badge>
      </h1>
      <p className="text-muted-foreground">
        {client.userData?.legalName || client.email}

      </p>
    </NavHeader>
  )
}
